import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { plans as plansApi } from '../api/client.js';
import PlanCalendar from './PlanCalendar.jsx';
import PlanCard from './PlanCard.jsx';
import { PlusIcon, CalendarIcon, ChevronIcon, NavigateIcon } from '../common/icons.jsx';

export default function PlanList() {
  const [plansList, setPlansList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [weekOffset, setWeekOffset] = useState(0);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (dateFilter && viewMode === 'list') params.date = dateFilter;
      setPlansList(await plansApi.list(params));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [dateFilter, viewMode]);

  const weekStart = getOffsetMonday(weekOffset);

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-xl font-bold text-[var(--color-primary)]">Tournées</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode(viewMode === 'list' ? 'calendar' : 'list')}
            className="flex items-center gap-1.5 bg-white border border-gray-200 text-[var(--color-neutral)] px-3 py-2 rounded-xl text-sm hover:bg-gray-50"
          >
            <CalendarIcon size={16} />
            <span className="hidden sm:inline">{viewMode === 'list' ? 'Calendrier' : 'Liste'}</span>
          </button>
          <Link
            to="/plans/suggest"
            className="flex items-center gap-1.5 bg-gradient-to-r from-[#1e40af] to-[#0891b2] text-white px-3 py-2 rounded-xl text-sm font-medium hover:opacity-90 shadow-sm min-h-[44px]"
          >
            <NavigateIcon size={16} />
            <span className="hidden sm:inline">Suggestion auto</span>
          </Link>
          <Link
            to="/plans/new"
            className="flex items-center gap-1.5 bg-[var(--color-orange)] text-white px-4 py-2 rounded-xl text-sm font-medium hover:opacity-90 shadow-sm min-h-[44px]"
          >
            <PlusIcon size={16} />
            <span>Créer</span>
          </Link>
        </div>
      </div>

      {/* Date filter (list mode) */}
      {viewMode === 'list' && (
        <div className="relative mb-4">
          <CalendarIcon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-[var(--color-accent)] focus:border-[var(--color-accent)] focus:outline-none"
          />
        </div>
      )}

      {/* Calendar week nav */}
      {viewMode === 'calendar' && (
        <div className="flex justify-between items-center mb-4 bg-white rounded-xl px-4 py-3 border border-gray-100">
          <button
            onClick={() => setWeekOffset(weekOffset - 1)}
            className="flex items-center gap-1 text-sm text-[var(--color-accent)] font-medium min-h-[44px] px-2"
          >
            <ChevronIcon size={16} className="rotate-180" />
            Prec.
          </button>
          <span className="text-sm font-semibold text-[var(--color-primary)]">{formatWeekRange(weekStart)}</span>
          <button
            onClick={() => setWeekOffset(weekOffset + 1)}
            className="flex items-center gap-1 text-sm text-[var(--color-accent)] font-medium min-h-[44px] px-2"
          >
            Suiv.
            <ChevronIcon size={16} />
          </button>
        </div>
      )}

      {error && (
        <p className="text-[var(--color-error)] bg-red-50 rounded-xl px-4 py-2.5 text-sm mb-4">{error}</p>
      )}

      {loading ? (
        <div className="text-center py-10">
          <div className="w-7 h-7 border-3 border-gray-200 border-t-[var(--color-accent)] rounded-full animate-spin mx-auto" />
        </div>
      ) : viewMode === 'calendar' ? (
        <PlanCalendar plans={plansList} weekStart={weekStart} />
      ) : plansList.length === 0 ? (
        <p className="text-[var(--color-neutral)] text-center py-10 text-sm">Aucune tournée. Créez-en une.</p>
      ) : (
        <div className="space-y-3">
          {plansList.map((plan) => (
            <PlanCard key={plan.id} plan={plan} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Get Monday date string offset by N weeks from current week.
 * @param {number} offset
 * @returns {string}
 */
function getOffsetMonday(offset) {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1) + offset * 7;
  now.setDate(diff);
  return now.toISOString().split('T')[0];
}

/**
 * Format a week range label from a Monday date string.
 * @param {string} mondayStr
 * @returns {string}
 */
function formatWeekRange(mondayStr) {
  const mon = new Date(mondayStr);
  const sun = new Date(mon);
  sun.setDate(sun.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  return `${fmt(mon)} - ${fmt(sun)}`;
}
